/**
 * FlickerText – tube-light flicker for headlines with hover re-trigger
 *
 * Splits text into characters and animates them in with a fluorescent “flicker on”
 * effect when the element enters the viewport. On mouse enter over a character,
 * only that character switches off briefly then flickers back on; others stay on.
 *
 * Use as an alternative to the text module for headlines:
 *   <h1 data-module="flickertext" data-start="hidden">Heading</h1>
 *
 * Requires: GSAP RoughEase (EasePack), SplitText from gsap.js
 */

import { Observe } from "./_/observe";
import gsap, { reduced, resetInitial, SplitText } from "../gsap";
import { RoughEase } from "gsap/EasePack";
import { computeParams } from "./_/index";

gsap.registerPlugin(RoughEase);

// Same ease as tube-light-flicker: jagged 0→1 with circ.out trend
const FLICKER_EASE =
  "rough({ template: circ.out, strength: 5, points: 10, taper: 'out', randomize: true, clamp: true })";

export class FlickerText extends Observe {
  #splitInstance;
  #chars = [];
  #inAnim;
  /** @type {Map<HTMLElement, gsap.core.Timeline>} */
  #charTimelines = new Map();
  #boundEnter;

  a = {
    duration: 0.75,
    delay: 0,
    stagger: 0.0625,
    hoverOffDuration: 0.12,
  };

  constructor(element) {
    super(element);
    this.create();
    resetInitial(element);
  }

  create() {
    if (reduced) return;

    const originalText = this.element.textContent || this.element.innerText;

    this.#splitInstance = SplitText.create(this.element, {
      type: "words,chars",
      charsClass: "char",
      wordsClass: "word",
      onSplit: (instance) => {
        this.#chars = instance.chars;
        gsap.set(this.#chars, { autoAlpha: 0 });
      },
    });

    if (originalText) {
      this.element.setAttribute("aria-label", originalText.trim());
    }

    computeParams(this.element, this.a);

    // Event delegation: only the character under the cursor reacts
    this.#boundEnter = (e) => this.#onCharEnter(e);
    this.element.addEventListener("mouseenter", this.#boundEnter, true);
  }

  #runFlickerOn(targets, opts = {}) {
    const { delay = 0 } = opts;
    return gsap.fromTo(
      targets,
      { autoAlpha: 0 },
      {
        autoAlpha: 1,
        duration: this.a.duration,
        stagger: this.a.stagger,
        ease: FLICKER_EASE,
        delay,
      }
    );
  }

  isIn = () => {
    if (reduced) return;
    if (this.#inAnim || !this.#chars.length) return;

    this.#inAnim = gsap.timeline().add(
      this.#runFlickerOn(this.#chars, { delay: this.a.delay }),
      0
    );
  };

  #onCharEnter(e) {
    if (reduced || !this.#chars.length) return;

    const charEl = this.#chars.includes(e.target) ? e.target : null;
    if (!charEl) return;

    const existing = this.#charTimelines.get(charEl);
    if (existing) existing.kill();

    const tl = gsap.timeline({
      onComplete: () => {
        this.#charTimelines.delete(charEl);
      },
    });

    this.#charTimelines.set(charEl, tl);

    // Switch off this char quickly
    tl.to(charEl, {
      autoAlpha: 0,
      duration: this.a.hoverOffDuration,
      ease: "power2.in",
    });

    // Flicker this char back on
    tl.add(this.#runFlickerOn([charEl]));
  }

  transitionOut() {
    this.element.removeEventListener("mouseenter", this.#boundEnter, true);
    this.#charTimelines.forEach((tl) => tl.kill());
    this.#charTimelines.clear();
    if (this.#inAnim) {
      this.#inAnim.kill();
      this.#inAnim = null;
    }
    if (this.#splitInstance) {
      this.#splitInstance.revert();
      this.#splitInstance = null;
    }
    this.#chars = [];
    this.destroy();
  }
}
